import { Input } from "../../../../components/common/input";
import { Button } from "../../../../components/common/button";
import { useFormFields } from "../../../../hooks/useFormFields";
import { useSubmitForm } from "../../../../hooks/useSubmitForm";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";
import { userService } from "../../../api_client";
import type {
  ChangePasswordDto,
  MessageResponseDto,
} from "../../../../api/api";

type ChangePasswordFormFields = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export function ChangePasswordSection() {
  const { formFields, createChangeHandler, resetFormFields } =
    useFormFields<ChangePasswordFormFields>({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });

  const { isLoading, errors, submitHandler } = useSubmitForm<
    ChangePasswordDto,
    MessageResponseDto
  >({
    submitMethod: async () => {
      // Validate that new password and confirm password match
      if (formFields.newPassword !== formFields.confirmPassword) {
        throw new Error("New password and confirm password do not match");
      }

      const changePasswordDto: ChangePasswordDto = {
        currentPassword: formFields.currentPassword,
        newPassword: formFields.newPassword,
      };

      return await userService.changePassword(changePasswordDto);
    },
    runAfterError: (error) => {
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ||
        (error as { message?: string })?.message ||
        "Failed to change password";
      showErrorToast(errorMessage);
    },
    runAfterSuccess: () => {
      showSuccessToast("Password changed successfully!");
      resetFormFields();
    },
  });

  const isFormValid =
    formFields.currentPassword.trim().length > 0 &&
    formFields.newPassword.trim().length > 0 &&
    formFields.confirmPassword.trim().length > 0 &&
    formFields.newPassword === formFields.confirmPassword;

  return (
    <form onSubmit={submitHandler} className="w-full">
      <div className="space-y-4">
        <Input
          type="password"
          label="Current Password"
          name="currentPassword"
          value={formFields.currentPassword}
          onChange={createChangeHandler("currentPassword")}
          placeholder="Enter current password"
          autoComplete="current-password"
          required
          error={errors?.currentPassword?.[0]}
        />

        <Input
          type="password"
          label="New Password"
          name="newPassword"
          value={formFields.newPassword}
          onChange={createChangeHandler("newPassword")}
          placeholder="Enter new password"
          autoComplete="new-password"
          required
          error={errors?.newPassword?.[0]}
        />

        <Input
          type="password"
          label="Confirm New Password"
          name="confirmPassword"
          value={formFields.confirmPassword}
          onChange={createChangeHandler("confirmPassword")}
          placeholder="Confirm new password"
          autoComplete="new-password"
          required
          error={
            formFields.newPassword !== formFields.confirmPassword &&
            formFields.confirmPassword.length > 0
              ? "Passwords do not match"
              : undefined
          }
        />

        <Button variant="primary"
          type="submit"
          className="w-full"
          disabled={isLoading || !isFormValid}
        >
          {isLoading ? "Changing..." : "Change Password"}
        </Button>
      </div>
    </form>
  );
}
